import type {
  FlagUpdate,
  Folder,
  MessageDetail,
  MessageFlag,
  MessageSummary,
  Paginated,
} from '@/api/types';

/**
 * Pure cache patches.
 *
 * **Nothing in this file imports React or TanStack Query.** These are plain
 * data-in/data-out transforms over the *shapes* the cache holds, and they are
 * shared by two callers (docs/plan/06-frontend-v1.md §1):
 *
 *  1. optimistic mutations (`src/hooks/useMessageMutations.ts`), and
 *  2. the live-update event reducer (`src/realtime/reducer.ts`, ADR 0002).
 *
 * Keeping them here is what makes the highest-risk logic in the app unit
 * testable without rendering anything — see `messagePatches.test.ts`.
 *
 * ## Structural sharing is part of the contract
 *
 * Every function returns the *identical reference* when nothing changed, and
 * reuses untouched sub-objects when something did. The message list is
 * virtualized over up to ~10k rows; returning a fresh array on a no-op patch
 * would re-render the window on every inbound event.
 */

/** The shape `useInfiniteQuery` stores for a message list. */
export interface MessagePageCache {
  pages: Paginated<MessageSummary>[];
  pageParams: unknown[];
}

/** Where a message was before a mutation moved or deleted it. */
export interface MessageOrigin {
  id: string;
  folderId: string;
  /** True when the message counted towards its folder's unread total. */
  wasUnread: boolean;
}

/** Signed changes to one folder's counters. */
export interface FolderCountDelta {
  unread?: number;
  total?: number;
}

export type FolderCountDeltas = Readonly<Record<string, FolderCountDelta>>;

/** One folder's counters as the *server* states them: absolute, not signed. */
export interface FolderCountTotals {
  unread: number;
  total: number;
}

/**
 * Overwrites one folder's counters with the server's own numbers.
 *
 * The delta form above is right for an optimistic mutation, which knows what it
 * changed but not what the folder now holds. A live event is the other way
 * round: `folder_counts`, `new_mail` and `expunge` all carry the counts *after*
 * the change, and turning those back into a delta against whatever the cache
 * happens to hold would reintroduce exactly the drift the absolute number
 * exists to end. Same structural-sharing contract as the rest of this file.
 */
export function setFolderCounts(
  folders: Folder[] | undefined,
  folderId: string,
  totals: FolderCountTotals,
): Folder[] | undefined {
  if (!folders) return folders;
  let changed = false;
  const next = folders.map((folder) => {
    if (folder.id !== folderId) return folder;
    const unreadCount = Math.max(0, Math.trunc(totals.unread));
    const totalCount = Math.max(0, Math.trunc(totals.total));
    if (unreadCount === folder.unreadCount && totalCount === folder.totalCount) return folder;
    changed = true;
    return { ...folder, unreadCount, totalCount };
  });
  return changed ? next : folders;
}

/* ------------------------------------------------------------------- flags */

/** Applies a `FlagUpdate` to a flag list, preserving order and deduplicating. */
export function applyFlagUpdate(
  flags: readonly MessageFlag[],
  update: FlagUpdate,
): MessageFlag[] {
  const removed = new Set(update.remove ?? []);
  const next = flags.filter((flag) => !removed.has(flag));
  for (const flag of update.add ?? []) {
    if (!next.includes(flag)) next.push(flag);
  }
  return next;
}

function sameFlags(a: readonly MessageFlag[], b: readonly MessageFlag[]): boolean {
  return a.length === b.length && a.every((flag, index) => flag === b[index]);
}

function patchSummaryFlags<T extends MessageSummary>(message: T, update: FlagUpdate): T {
  const flags = applyFlagUpdate(message.flags, update);
  return sameFlags(message.flags, flags) ? message : { ...message, flags };
}

/**
 * Patches message flags across every page of one cached list.
 *
 * Used by the flag/read mutations and by the `message.flags` WebSocket event.
 */
export function patchMessageFlags(
  cache: MessagePageCache | undefined,
  messageIds: readonly string[],
  update: FlagUpdate,
  accountId?: string,
): MessagePageCache | undefined {
  if (!cache) return cache;
  const ids = new Set(messageIds);
  return mapPages(cache, (message) =>
    ids.has(message.id) && ownedBy(message, accountId)
      ? patchSummaryFlags(message, update)
      : message,
  );
}

/**
 * Whether a cached row belongs to the account a patch is about.
 *
 * A message id embeds no account (ADR 0012), so the same string can name a
 * different message in two mailboxes — which it does the moment a mailbox is
 * attached to itself. In a per-account cache this is always true and costs
 * nothing; in the unified list (ADR 0056) it is the whole guard.
 */
function ownedBy(message: MessageSummary, accountId: string | undefined): boolean {
  return accountId === undefined || message.accountId === accountId;
}

/** The same patch against an open message detail. */
export function patchMessageDetailFlags(
  detail: MessageDetail | undefined,
  messageIds: readonly string[],
  update: FlagUpdate,
): MessageDetail | undefined {
  if (!detail || !messageIds.includes(detail.id)) return detail;
  return patchSummaryFlags(detail, update);
}

/* ----------------------------------------------------------------- labels */

/**
 * A change to messages' label keywords (plan/17 §5). Data only — it crosses
 * windows on the cache channel — and compared the way the server compares:
 * ASCII case-insensitively, so removing `$label_Work` also removes a
 * `$label_work` the server happened to spell that way.
 *
 * - `add` / `remove`: a change someone made (the Label-as picker, a chip ×).
 * - `set`: the whole set a message has NOW — the live stream's reading.
 * - `rename`: every `from` becomes `to` (a finished rename).
 */
export interface LabelKeywordEdit {
  add?: readonly string[];
  remove?: readonly string[];
  set?: readonly string[];
  rename?: { from: string; to: string };
}

function foldAscii(value: string): string {
  return value.replace(/[A-Z]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 32));
}

function hasKeyword(keywords: readonly string[], keyword: string): boolean {
  const folded = foldAscii(keyword);
  return keywords.some((candidate) => foldAscii(candidate) === folded);
}

/** The keywords after `edit`; the SAME array when nothing changed. */
export function applyLabelKeywordEdit(
  current: readonly string[] | undefined,
  edit: LabelKeywordEdit,
): string[] {
  const before = (current ?? []) as string[];
  let next: string[];
  if (edit.set !== undefined) {
    next = [...edit.set];
  } else {
    next = before.filter((keyword) => !hasKeyword(edit.remove ?? [], keyword));
    if (edit.rename && hasKeyword(next, edit.rename.from)) {
      next = next.filter((keyword) => foldAscii(keyword) !== foldAscii(edit.rename!.from));
      if (!hasKeyword(next, edit.rename.to)) next.push(edit.rename.to);
    }
    for (const keyword of edit.add ?? []) if (!hasKeyword(next, keyword)) next.push(keyword);
  }
  const same = next.length === before.length && next.every((keyword, index) => keyword === before[index]);
  return same && current !== undefined ? before : next;
}

function patchSummaryLabels<T extends MessageSummary>(message: T, edit: LabelKeywordEdit): T {
  const labelKeywords = applyLabelKeywordEdit(message.labelKeywords, edit);
  return labelKeywords === message.labelKeywords ? message : { ...message, labelKeywords };
}

/**
 * Patches label keywords across every page of one cached list. `messageIds`
 * `undefined` means every row of the account (a finished rename or delete).
 */
export function patchMessageLabels(
  cache: MessagePageCache | undefined,
  messageIds: readonly string[] | undefined,
  edit: LabelKeywordEdit,
  accountId?: string,
): MessagePageCache | undefined {
  if (!cache) return cache;
  const ids = messageIds ? new Set(messageIds) : undefined;
  return mapPages(cache, (message) =>
    (ids === undefined || ids.has(message.id)) && ownedBy(message, accountId)
      ? patchSummaryLabels(message, edit)
      : message,
  );
}

/** The same patch against an open message detail. */
export function patchMessageDetailLabels(
  detail: MessageDetail | undefined,
  messageIds: readonly string[] | undefined,
  edit: LabelKeywordEdit,
): MessageDetail | undefined {
  if (!detail || (messageIds !== undefined && !messageIds.includes(detail.id))) return detail;
  return patchSummaryLabels(detail, edit);
}

/* ---------------------------------------------------------------- removal */

/**
 * Drops rows from every page of a cached list, keeping `total` honest.
 *
 * This is the patch behind delete, move and archive: the row leaves the list it
 * is currently in. It is deliberately *not* an `invalidateQueries` — refetching
 * a visible list re-renders every virtualized row and jumps the scroll position
 * out from under the cursor (§5).
 */
export function removeMessageFromList(
  cache: MessagePageCache | undefined,
  messageIds: readonly string[],
  accountId?: string,
): MessagePageCache | undefined {
  if (!cache) return cache;
  const ids = new Set(messageIds);
  let changed = false;

  const pages = cache.pages.map((page) => {
    const items = page.items.filter(
      (message) => !(ids.has(message.id) && ownedBy(message, accountId)),
    );
    if (items.length === page.items.length) return page;
    changed = true;
    const dropped = page.items.length - items.length;
    return {
      ...page,
      items,
      ...(page.total !== undefined ? { total: Math.max(0, page.total - dropped) } : {}),
    };
  });

  return changed ? { ...cache, pages } : cache;
}

/** Retargets rows/details at a new folder — the non-destructive half of a move. */
export function patchMessageFolder(
  detail: MessageDetail | undefined,
  messageIds: readonly string[],
  targetFolderId: string,
): MessageDetail | undefined {
  if (!detail || !messageIds.includes(detail.id)) return detail;
  if (detail.folderId === targetFolderId) return detail;
  return { ...detail, folderId: targetFolderId };
}

/* ----------------------------------------------------------------- counts */

/**
 * Applies signed deltas to the folder tree's counters, clamped at zero.
 *
 * Counts are patched rather than refetched so the sidebar cannot flicker back
 * to the pre-mutation number while a refetch is in flight.
 */
export function adjustUnreadCount(
  folders: Folder[] | undefined,
  deltas: FolderCountDeltas,
): Folder[] | undefined {
  if (!folders) return folders;
  const entries = Object.entries(deltas).filter(
    ([, delta]) => (delta.unread ?? 0) !== 0 || (delta.total ?? 0) !== 0,
  );
  if (entries.length === 0) return folders;
  const byId = new Map(entries);

  let changed = false;
  const next = folders.map((folder) => {
    const delta = byId.get(folder.id);
    if (!delta) return folder;
    const unreadCount = Math.max(0, folder.unreadCount + (delta.unread ?? 0));
    const totalCount = Math.max(0, folder.totalCount + (delta.total ?? 0));
    if (unreadCount === folder.unreadCount && totalCount === folder.totalCount) return folder;
    changed = true;
    return { ...folder, unreadCount, totalCount };
  });

  return changed ? next : folders;
}

/* ---------------------------------------------------------------- origins */

/**
 * Records where each message currently lives, before a mutation moves it.
 *
 * Two consumers: the count maths below (an unread message leaving a folder
 * decrements *that* folder), and the inverse mutation behind undo (§5) — undo
 * is a move back to the recorded folder, not a cache rollback.
 */
export function recordSourceFolder(messages: readonly MessageSummary[]): MessageOrigin[] {
  return messages.map((message) => ({
    id: message.id,
    folderId: message.folderId,
    wasUnread: !message.flags.includes('seen'),
  }));
}

/** Folder counter deltas for a set of messages leaving their folder. */
export function countDeltasForRemoval(
  origins: readonly MessageOrigin[],
  targetFolderId?: string,
): FolderCountDeltas {
  const deltas: Record<string, FolderCountDelta> = {};
  const bump = (folderId: string, unread: number, total: number) => {
    const current = deltas[folderId] ?? { unread: 0, total: 0 };
    deltas[folderId] = {
      unread: (current.unread ?? 0) + unread,
      total: (current.total ?? 0) + total,
    };
  };

  for (const origin of origins) {
    if (origin.folderId === targetFolderId) continue;
    bump(origin.folderId, origin.wasUnread ? -1 : 0, -1);
    if (targetFolderId) bump(targetFolderId, origin.wasUnread ? 1 : 0, 1);
  }
  return deltas;
}

/** Folder counter deltas for a flag change that adds or removes `seen`. */
export function countDeltasForFlagUpdate(
  origins: readonly MessageOrigin[],
  update: FlagUpdate,
): FolderCountDeltas {
  const marksRead = (update.add ?? []).includes('seen');
  const marksUnread = (update.remove ?? []).includes('seen');
  if (marksRead === marksUnread) return {}; // neither, or a contradictory update

  const deltas: Record<string, FolderCountDelta> = {};
  for (const origin of origins) {
    // Marking an already-read message read changes no counter.
    if (marksRead && !origin.wasUnread) continue;
    if (marksUnread && origin.wasUnread) continue;
    const current = deltas[origin.folderId]?.unread ?? 0;
    deltas[origin.folderId] = { unread: current + (marksRead ? -1 : 1) };
  }
  return deltas;
}

/* ----------------------------------------------------------------- shared */

function mapPages(
  cache: MessagePageCache,
  fn: (message: MessageSummary) => MessageSummary,
): MessagePageCache {
  let changed = false;
  const pages = cache.pages.map((page) => {
    let pageChanged = false;
    const items = page.items.map((message) => {
      const next = fn(message);
      if (next !== message) pageChanged = true;
      return next;
    });
    if (!pageChanged) return page;
    changed = true;
    return { ...page, items };
  });
  return changed ? { ...cache, pages } : cache;
}
